import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import defaultTheme, { themes } from '../../themes';
import { modeColors } from './utils';

// Add keyframe animation for continuous bouncing
const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
`;

export const Container = styled.div`
  text-align: center;
  background-color: ${defaultTheme.colors.background};
  border-radius: 30px;
  padding: 30px 20px;
  box-shadow: 0 8px 16px rgba(123, 63, 0, 0.2);
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  overflow: visible;
  border: 8px solid ${defaultTheme.colors.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

export const Title = styled.h1`
  color: ${defaultTheme.colors.primary};
  font-size: 54px;
  margin-bottom: 10px;
  font-family: ${defaultTheme.fonts.title};
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
  background: linear-gradient(to right, ${defaultTheme.colors.primary}, #A86B2D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 24px;
  position: relative;
  z-index: 2;
`;

export const Button = styled(motion.button)`
  padding: 20px 40px;
  font-size: 24px;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  background-color: #FFD93D;
  color: #7B3F00;
  font-family: 'Comic Sans MS', cursive, sans-serif;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 3px solid #F5E1A4;
  transition: all 0.3s ease;

  &:hover {
    background-color: #FFE566;
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: #E2C48D;
    cursor: not-allowed;
    border-color: #D4B67A;
  }
`;

export const ModeContainer = styled.div`
  display: flex;
  background: rgba(255, 245, 230, 0.9);
  padding: 6px;
  border-radius: 20px;
  gap: 4px;
  margin-bottom: 16px;
  position: relative;
  z-index: 2;
  justify-content: center;
  box-shadow: inset 0 2px 4px rgba(123, 63, 0, 0.1);
  border: 2px solid #F5E1A4;
`;

export const ModeButton = styled(motion.button)<{ isActive?: boolean }>`
  padding: 12px 24px;
  font-size: 18px;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  background: ${props => props.isActive ? 
    'linear-gradient(180deg, #FFD93D 0%, #FFB302 100%)' : 
    'transparent'};
  color: #7B3F00;
  font-family: 'Fredoka One', 'Comic Sans MS', cursive, sans-serif;
  font-weight: bold;
  box-shadow: ${props => props.isActive ? 
    '0 2px 4px rgba(123, 63, 0, 0.2)' : 
    'none'};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  opacity: ${props => props.isActive ? 1 : 0.7};
  transform: ${props => props.isActive ? 'scale(1)' : 'scale(0.95)'};

  &:hover {
    opacity: 1;
    background: ${props => props.isActive ? 
      'linear-gradient(180deg, #FFD93D 0%, #FFB302 100%)' : 
      'rgba(255, 217, 61, 0.2)'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const TimerWrapper = styled.div`
  position: relative;
  width: 440px;
  height: 440px;
  margin: 10px auto 40px auto;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
`;

export const TimerText = styled(motion.div)<{ isRunning?: boolean }>`
  position: absolute;
  inset: 0;
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 11;
  pointer-events: none;
  user-select: none;
  font-size: 80px;
  font-weight: 900;
  color: #fff;
  text-shadow: 2px 2px 8px rgba(0,0,0,0.5);
  font-family: 'Fredoka One, Comic Sans MS, cursive, sans-serif';
  line-height: 1;
`;

export const Card = styled.div`
  background-color: rgba(255, 245, 230, 0.95);
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(123, 63, 0, 0.13);
  border: 3px solid #F5E1A4;
  padding: 28px 24px 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 220px;
`;

export const SectionTitle = styled.div`
  font-family: 'Fredoka One', 'Comic Sans MS', cursive, sans-serif;
  color: #7B3F00;
  font-size: 26px;
  margin-bottom: 18px;
  text-align: center;
`;

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  margin-bottom: 32px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const StatLabel = styled.div`
  font-size: 16px;
  color: #7B3F00;
  margin-bottom: 5px;
  font-weight: bold;
`;

export const StatValue = styled.div<{mode: keyof typeof modeColors}>`
  font-size: 24px;
  color: #7B3F00;
  font-weight: bold;
  background-color: ${({mode}) => modeColors[mode]};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const AppRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  min-height: 100vh;
  background: none;
  gap: 0;
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 10px 0;
  position: relative;
  gap: 10px;
`;

export const CompletionStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: rgba(255, 245, 230, 0.8);
  border-radius: 15px;
  padding: 20px 15px;
  box-shadow: 0 4px 8px rgba(123, 63, 0, 0.15);
  border: 3px solid #F5E1A4;
  min-width: 170px;
`;

export const RightRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: 220px;
  background: rgba(255,245,230,0.8);
  border-radius: 15px;
  border: 3px solid #F5E1A4;
  box-shadow: 0 4px 8px rgba(123, 63, 0, 0.10);
  padding: 18px 10px 18px 10px;
  height: fit-content;
`;

export const CalendarTitle = styled.div`
  font-family: 'Fredoka One', 'Comic Sans MS', cursive, sans-serif;
  color: #7B3F00;
  font-size: 22px;
  margin-bottom: 10px;
  text-align: center;
`;

export const CalendarContainer = styled.div`
  width: 100%;
  .react-calendar {
    background: rgba(255,245,230,0.8);
    border-radius: 15px;
    border: none !important;
    box-shadow: none !important;
    padding: 10px 0 10px 0;
    font-family: 'Fredoka One', 'Comic Sans MS', cursive, sans-serif;
    color: #7B3F00;
    font-size: 1.25rem;
    width: 100%;
    min-width: 350px;
    max-width: 100%;
  }
  .react-calendar__navigation__label {
    font-size: 1.4rem;
    font-weight: bold;
    color: #7B3F00;
    letter-spacing: 1px;
  }
  .react-calendar__tile {
    min-height: 80px;
    min-width: 48px;
    position: relative;
    font-size: 1.1rem;
    border-radius: 14px;
    transition: background 0.2s;
  }
  .react-calendar__tile--now {
    background: #FFD93D !important;
    color: #7B3F00 !important;
    min-width: 64px !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
    box-sizing: border-box;
  }
  .react-calendar__tile--active {
    background: #FF6B6B !important;
    color: #fff !important;
  }
  .react-calendar__month-view__days__day {
    padding: 0;
  }
`;

export const CountsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 2px;
  margin-top: 2px;
`;

export const CountDot = styled.div<{color: string}>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({color}) => color};
  color: #fff;
  font-size: 13px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.10);
`;

export const SprinkleBackground = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
  pointer-events: none;
  background-image: url('https://www.transparenttextures.com/patterns/sprinkles.png');
  opacity: 0.15;
`;

export const Awning = styled.div`
  width: 100%;
  height: 60px;
  background: repeating-linear-gradient(
    90deg,
    #FFD93D 0 40px,
    #FF6B6B 40px 80px
  );
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  margin-bottom: -20px;
  box-shadow: 0 4px 8px rgba(123, 63, 0, 0.08);
  position: relative;
  z-index: 3;
`;

export const TomatoContainer = styled.g<{ isRunning: boolean }>`
  animation: ${bounce} 2.5s ease-in-out infinite;
  animation-play-state: ${props => props.isRunning ? 'running' : 'paused'};
  transform-origin: center;
`; 